import {
  Badge,
  Box,
  Button,
  Code,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuArrowRight,
  LuCircleCheck,
  LuCopy,
  LuDownload,
  LuFileText,
  LuLayoutGrid,
  LuLightbulb,
  LuMail,
  LuMicroscope,
  LuMousePointerClick,
  LuPencil,
  LuRefreshCw,
  LuRocket,
  LuScanLine,
  LuSearch,
  LuSparkles,
  LuTarget,
  LuUpload,
  LuX,
  LuZap,
} from 'react-icons/lu'
import { hasAcknowledgedAIAct } from '../../_shared/AIActNotice'
import { useUiPreferences, useUpdateUiPreferences } from '../../../api/api_hooks/user/uiPreferences'

const STORAGE_KEY = 'careflai.blotTourCompleted.v1'
const PAD = 10
const TOOLTIP_W = 380
const TOOLTIP_W_WIDE = 480 // scanByEmail step — needs room for the full address + copy button
const TOOLTIP_GAP = 16

export type TourViewMode = 'immunoblot-selection' | 'recent-cases' | 'new-analysis' | 'case-details'

export function hasSeenBlotTour(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1' } catch { return false }
}
function markBlotTourSeen() {
  try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* ignore */ }
}

// A coachmark step. `viewMode` says which view the spotlight lives in.
// If user is on the WRONG view, we surface a "go to <view>" instruction
// instead of the spotlight (the target element doesn't exist there).
type CoachStep = {
  id: string
  viewMode: TourViewMode
  targetId?: string
  icon: any
  titleKey: string
  bodyKey: string
  /** Optional one-line instruction shown when the user must perform an action to progress. */
  actionHintKey?: string
}

const STEPS: CoachStep[] = [
  { id: 'catalog',     viewMode: 'immunoblot-selection', targetId: 'blot-catalog',      icon: LuMicroscope,       titleKey: 'blotAnalysis.tour.step.catalog.title',      bodyKey: 'blotAnalysis.tour.step.catalog.body' },
  { id: 'search',      viewMode: 'immunoblot-selection', targetId: 'blot-search',       icon: LuSearch,           titleKey: 'blotAnalysis.tour.step.search.title',       bodyKey: 'blotAnalysis.tour.step.search.body' },
  { id: 'newAnalysis', viewMode: 'recent-cases',         targetId: 'btn-new-analysis',  icon: LuZap,              titleKey: 'blotAnalysis.tour.step.newAnalysis.title',  bodyKey: 'blotAnalysis.tour.step.newAnalysis.body',  actionHintKey: 'blotAnalysis.tour.action.selectTest' },
  // Tip-format step (no spotlight target) — highlights the scan-by-email
  // shortcut before the user starts the manual upload path.
  { id: 'scanByEmail', viewMode: 'recent-cases',         icon: LuMail,                                            titleKey: 'blotAnalysis.tour.step.scanByEmail.title',  bodyKey: 'blotAnalysis.tour.step.scanByEmail.body' },
  { id: 'upload',      viewMode: 'new-analysis',         targetId: 'blot-upload',       icon: LuUpload,           titleKey: 'blotAnalysis.tour.step.upload.title',       bodyKey: 'blotAnalysis.tour.step.upload.body',       actionHintKey: 'blotAnalysis.tour.action.openNewAnalysis' },
  { id: 'analyze',     viewMode: 'new-analysis',         targetId: 'btn-analyze',       icon: LuScanLine,         titleKey: 'blotAnalysis.tour.step.analyze.title',      bodyKey: 'blotAnalysis.tour.step.analyze.body',      actionHintKey: 'blotAnalysis.tour.action.uploadFile' },
  // Combined case-details step — the previous sourceImage / gallery /
  // fabDock trio required a real analysis to be worth showing. We now
  // show a single simulation-style card listing what the results screen
  // offers (validation, calibration, export…) so the tour is complete
  // even without a case to click into.
  { id: 'results',     viewMode: 'case-details',         icon: LuLayoutGrid,                                      titleKey: 'blotAnalysis.tour.step.results.title',      bodyKey: 'blotAnalysis.tour.step.results.body',      actionHintKey: 'blotAnalysis.tour.action.runAnalysis' },
]

// Views the tour can auto-switch to when the user hits "Suivant" — must be
// safe to enter with no pre-conditions. case-details requires a selected
// case, so it stays user-driven (falls back to the action hint).
const AUTO_NAV_VIEWS: TourViewMode[] = ['immunoblot-selection', 'recent-cases', 'new-analysis']

interface BlotOnboardingTourProps {
  /** Called when the user hits "Suivant" and the next step's view differs
   *  from the current one — the parent switches the workspace view so the
   *  tour advances into the real screen, not another text-only tooltip.
   *  Only invoked for views in AUTO_NAV_VIEWS. */
  onChangeView?: (view: TourViewMode) => void
  /** Current view in the workspace — drives auto-advance through the walkthrough. */
  currentView: TourViewMode
  /** Controlled tour phase, owned by the parent so it survives view transitions.
   *  -1 = closed, 0 = welcome modal, 1..STEPS.length = coachmarks, STEPS.length+1 = final modal.
   *  Parent triggers the tour imperatively by setting phase to 0. */
  phase: number
  setPhase: (updater: number | ((p: number) => number)) => void
  /** True while the "What's New" modal is open — holds off the tour's own
   *  auto-open until it closes. On a brand-new account both modals' "never
   *  seen this" conditions are true at the same time (one gated server-side
   *  via uiPreferences, the other via a separate localStorage key, with no
   *  shared state), so without this they mounted on top of each other —
   *  WhatsNew opens synchronously on mount, the tour ~500ms later, stacking
   *  since the tour's overlay z-index (1500/1501) sits above WhatsNew's
   *  default Modal z-index. */
  blockedByWhatsNew?: boolean
}

export default function BlotOnboardingTour({ currentView, phase, setPhase, onChangeView, blockedByWhatsNew }: BlotOnboardingTourProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [tooltipH, setTooltipH] = useState<number>(320)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const phaseRef = useRef(phase)
  phaseRef.current = phase

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const { data: prefs, isLoading: prefsLoading } = useUiPreferences()
  const { mutateAsync: updatePrefs } = useUpdateUiPreferences()
  const completedOnServer = !!prefs?.blotTourCompletedAt

  // First-visit auto-open. Server prefs are authoritative — if the server
  // says NOT completed we always show, ignoring the per-browser localStorage
  // flag which would otherwise block a fresh user logging into a browser
  // where a previous account had already dismissed the tour. Same reasoning
  // for the AI Act ack: honour either the server value or the local flag so
  // we don't poll forever when the user acked from another device.
  useEffect(() => {
    if (phaseRef.current !== -1) return
    if (prefsLoading) return
    if (completedOnServer) return
    if (blockedByWhatsNew) return

    const aiActAckOnServer = !!prefs?.aiActAcknowledgedAt

    let cancelled = false
    let poll: number | undefined
    const tryOpen = () => {
      if (cancelled) return
      if (!aiActAckOnServer && !hasAcknowledgedAIAct()) {
        poll = window.setTimeout(tryOpen, 800)
        return
      }
      window.setTimeout(() => { if (!cancelled && phaseRef.current === -1) setPhase(0) }, 500)
    }
    tryOpen()
    return () => {
      cancelled = true
      if (poll) window.clearTimeout(poll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoading, completedOnServer, prefs?.aiActAcknowledgedAt, blockedByWhatsNew])

  // ── Auto-advance when the user actually progresses through views. ──
  // We jump FORWARD to the first matching step >= current phase; we never
  // retreat (using "Back" is opt-in). Example: user is on the catalog step
  // and clicks a test → view becomes recent-cases → tour jumps to the
  // "New Analysis" coachmark instead of getting stuck.
  useEffect(() => {
    const p = phaseRef.current
    if (p < 1 || p > STEPS.length) return
    const currentStep = STEPS[p - 1]
    if (currentStep.viewMode === currentView) return
    const nextIdx = STEPS.findIndex((s, i) => i >= p - 1 && s.viewMode === currentView)
    if (nextIdx !== -1) setPhase(nextIdx + 1)
    // If no match found later in the list, leave phase as-is and the tooltip
    // will degrade gracefully into a "navigate back" hint.
  }, [currentView])

  // Resolve & track the current coachmark target.
  const currentCoach = phase >= 1 && phase <= STEPS.length ? STEPS[phase - 1] : null
  const targetId = currentCoach?.targetId
  const targetMatchesView = currentCoach?.viewMode === currentView

  useLayoutEffect(() => {
    if (!targetId || !targetMatchesView) { setRect(null); return }
    const el = document.querySelector<HTMLElement>(`[data-tour-id="${targetId}"]`)
    if (!el) { setRect(null); return }
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    const measure = () => setRect(el.getBoundingClientRect())
    const t1 = window.setTimeout(measure, 350)
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.clearTimeout(t1)
      ro.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [targetId, targetMatchesView])

  const finish = useCallback((markSeen = true) => {
    if (markSeen) {
      markBlotTourSeen()
      // Best-effort server-side persistence — the local flag already
      // suppresses the next mount; the BE write makes the dismissal
      // stick across devices and storage wipes.
      updatePrefs({ blotTourCompletedAt: new Date().toISOString() }).catch(() => {})
    }
    setPhase(-1)
    setRect(null)
    // Send the user back to the catalog on close — the tour may have
    // auto-navigated them to new-analysis, but with no selected test the
    // upload screen is a dead end. immunoblot-selection is always the
    // right entry point.
    onChangeView?.('immunoblot-selection')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChangeView])

  const next = () => setPhase(p => {
    const nextP = Math.min(p + 1, STEPS.length + 1)
    // If the next step lives in a safe auto-nav view different from the
    // current one, ask the parent to switch view so the user actually
    // sees the new screen (instead of another text-only tooltip on the
    // same page). case-details is intentionally excluded — it needs a
    // real selected case, so the hint stays user-driven.
    const target = STEPS[nextP - 1]
    if (target && onChangeView && target.viewMode !== currentView && AUTO_NAV_VIEWS.includes(target.viewMode)) {
      onChangeView(target.viewMode)
    }
    return nextP
  })
  const prev = () => setPhase(p => Math.max(p - 1, 0))

  // ────────── render ──────────
  if (phase === -1) return null

  // Phase 0 — welcome modal
  if (phase === 0) {
    return <WelcomeModal onStart={() => setPhase(1)} onSkip={() => finish(true)} />
  }

  // Phase STEPS.length+1 — closing modal
  if (phase === STEPS.length + 1) {
    return <FinishedModal onDone={() => finish(true)} />
  }

  // Phases 1..STEPS.length — coachmark
  const step = STEPS[phase - 1]
  const total = STEPS.length
  const title = t(step.titleKey)
  const body = t(step.bodyKey)
  const showHint = !targetMatchesView && !!step.actionHintKey
  const hint = showHint ? t(step.actionHintKey!) : null

  // Some steps (scanByEmail) render a long inline address and need extra
  // horizontal room so it doesn't get truncated by the ellipsis on Code.
  const tooltipW = step.id === 'scanByEmail' ? TOOLTIP_W_WIDE : TOOLTIP_W

  // Tooltip placement: below target if room, else above; no anchor → centered
  const tooltipPos = computeTooltipPos(rect, isMobile, tooltipH, tooltipW)

  return (
    <>
      {/* Backdrop with spotlight cutout (box-shadow trick). When no target, dim the whole screen. */}
      <Box
        position="fixed"
        zIndex={1500}
        pointerEvents="auto"
        {...(rect && {
          top: `${rect.top - PAD}px`,
          left: `${rect.left - PAD}px`,
          width: `${rect.width + PAD * 2}px`,
          height: `${rect.height + PAD * 2}px`,
          borderRadius: '14px',
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.62)',
          transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          outline: '2px solid rgba(20, 184, 166, 0.85)',
          outlineOffset: '2px',
        })}
        {...(!rect && {
          top: 0, left: 0, right: 0, bottom: 0,
          bg: 'blackAlpha.700',
          sx: { backdropFilter: 'blur(4px)' },
        })}
      />

      {/* Tooltip */}
      <Box
        ref={(el: HTMLDivElement | null) => {
          tooltipRef.current = el
          if (el) {
            const h = el.getBoundingClientRect().height
            if (h > 0 && Math.abs(h - tooltipH) > 4) setTooltipH(h)
          }
        }}
        position="fixed"
        zIndex={1501}
        top={`${tooltipPos.top}px`}
        left={`${tooltipPos.left}px`}
        width={`${tooltipW}px`}
        maxW="calc(100vw - 24px)"
        maxH="calc(100vh - 24px)"
        bg="white"
        borderRadius="2xl"
        boxShadow="0 24px 64px -12px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(15,23,42,0.08)"
        overflowY="auto"
        overflowX="hidden"
        transition="all 0.3s cubic-bezier(0.22, 1, 0.36, 1)"
      >
        {/* Header strip — gradient teal→cyan with pastille step number */}
        <Box
          bgGradient="linear(135deg, teal.600 0%, teal.500 50%, cyan.400 100%)"
          px={4}
          py={3}
          position="relative"
        >
          <HStack justify="space-between" align="center" position="relative" zIndex={1}>
            <HStack spacing={3}>
              <Box
                w="34px"
                h="34px"
                borderRadius="full"
                bg="whiteAlpha.300"
                border="1px solid"
                borderColor="whiteAlpha.500"
                color="white"
                display="grid"
                sx={{ placeItems: 'center' }}
                fontWeight="800"
                fontSize="sm"
                backdropFilter="blur(8px)"
              >
                {phase}
              </Box>
              <Text fontSize="xs" color="whiteAlpha.900" fontWeight="600" letterSpacing="wider" textTransform="uppercase">
                {t('blotAnalysis.tour.stepCounter', { current: phase, total })}
              </Text>
            </HStack>
            <IconButton
              aria-label={t('blotAnalysis.tour.skip')}
              icon={<LuX />}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              onClick={() => finish(true)}
            />
          </HStack>
          <Box
            position="absolute" top={0} left={0} right={0} bottom={0}
            background="radial-gradient(circle at 80% 20%, rgba(255,255,255,0.25) 0%, transparent 55%)"
            pointerEvents="none"
          />
        </Box>

        {/* Body */}
        <VStack align="stretch" spacing={3} px={5} py={4}>
          <HStack spacing={3} align="start">
            <Box
              w="40px" h="40px" minW="40px"
              borderRadius="xl"
              bgGradient="linear(135deg, teal.50, cyan.50)"
              border="1px solid"
              borderColor="teal.100"
              display="grid"
              sx={{ placeItems: 'center' }}
              color="teal.600"
            >
              <Icon as={step.icon} boxSize={5} />
            </Box>
            <VStack align="start" spacing={1} flex={1}>
              <Heading size="sm" color="gray.900" lineHeight="1.3">{title}</Heading>
            </VStack>
          </HStack>
          {renderStepBody(step, body, t, toast)}

          {/* Contextual action hint shown when the user is not yet in the right view */}
          {hint && (
            <HStack
              spacing={2}
              px={3}
              py={2.5}
              bgGradient="linear(135deg, teal.50, cyan.50)"
              borderRadius="lg"
              border="1px dashed"
              borderColor="teal.200"
              align="start"
            >
              <Icon as={LuMousePointerClick} color="teal.600" mt={0.5} flexShrink={0} />
              <Text fontSize="xs" color="teal.800" fontWeight="600" lineHeight="1.5">
                {hint}
              </Text>
            </HStack>
          )}

          {/* progress dots */}
          <HStack spacing={1.5} pt={1} justify="center">
            {STEPS.map((_, i) => (
              <Box
                key={i}
                w={i + 1 === phase ? '20px' : '6px'}
                h="6px"
                borderRadius="full"
                bg={i + 1 === phase ? 'teal.500' : i + 1 < phase ? 'teal.200' : 'gray.200'}
                transition="all 0.25s ease"
              />
            ))}
          </HStack>
        </VStack>

        {/* Footer actions */}
        <HStack justify="space-between" px={5} py={3} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" size="sm" color="gray.500" onClick={() => finish(true)}>
            {t('blotAnalysis.tour.skip')}
          </Button>
          <HStack spacing={2}>
            {phase > 1 && (
              <Button size="sm" variant="outline" onClick={prev} colorScheme="gray">
                {t('blotAnalysis.tour.prev')}
              </Button>
            )}
            <Button
              size="sm"
              colorScheme="teal"
              rightIcon={<LuArrowRight />}
              onClick={next}
              bgGradient="linear(135deg, teal.500, cyan.500)"
              _hover={{ bgGradient: 'linear(135deg, teal.600, cyan.600)' }}
            >
              {phase === STEPS.length ? t('blotAnalysis.tour.finish') : t('blotAnalysis.tour.next')}
            </Button>
          </HStack>
        </HStack>
      </Box>
    </>
  )
}

// ── helpers ──────────────────────────────────────────────────────────────

/**
 * Renders the body of a coachmark. Most steps just show the i18n text;
 * a few (scanByEmail tip, results simulation) override with richer JSX
 * so the tour can educate about features that don't have a live target
 * on-screen (e.g. the ScanInboxPanel lives in /my-info, and the case
 * details view requires a real analysis to see anything meaningful).
 */
function renderStepBody(
  step: CoachStep,
  body: string,
  t: (k: string) => string,
  toast: ReturnType<typeof useToast>,
) {
  if (step.id === 'scanByEmail') {
    const address = 'scan@scan.careflai.com'
    return (
      <VStack align="stretch" spacing={3}>
        <HStack
          bg="teal.50"
          border="1px dashed"
          borderColor="teal.300"
          borderRadius="lg"
          px={2.5}
          py={2}
          spacing={2}
          align="start"
        >
          <Icon as={LuLightbulb} color="teal.600" mt={0.5} flexShrink={0} />
          <Text fontSize="xs" color="teal.900" fontWeight="600">
            {t('blotAnalysis.tour.step.scanByEmail.tip')}
          </Text>
        </HStack>
        <Text fontSize="sm" color="gray.700" lineHeight="1.6">{body}</Text>
        <VStack
          align="stretch"
          spacing={1.5}
          bgGradient="linear(135deg, teal.50, cyan.50)"
          border="1px solid"
          borderColor="teal.200"
          borderRadius="xl"
          p={3}
        >
          <Text fontSize="2xs" color="teal.700" fontWeight="700" letterSpacing="wider" textTransform="uppercase">
            {t('blotAnalysis.tour.step.scanByEmail.addressLabel')}
          </Text>
          <HStack justify="space-between" align="center" spacing={2}>
            <Code
              fontSize="md"
              fontWeight="700"
              bg="white"
              color="teal.700"
              px={2.5}
              py={1}
              borderRadius="md"
              flex={1}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {address}
            </Code>
            <Button
              size="xs"
              leftIcon={<Icon as={LuCopy} />}
              colorScheme="teal"
              variant="solid"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(address)
                  toast({ title: t('blotAnalysis.tour.step.scanByEmail.copied'), status: 'success', duration: 1500 })
                } catch {
                  /* ignore */
                }
              }}
            >
              {t('blotAnalysis.tour.step.scanByEmail.copy')}
            </Button>
          </HStack>
        </VStack>
      </VStack>
    )
  }

  if (step.id === 'results') {
    const actions: Array<{ icon: any; labelKey: string }> = [
      { icon: LuTarget,    labelKey: 'blotAnalysis.tour.step.results.mock.addBlot' },
      { icon: LuPencil,    labelKey: 'blotAnalysis.tour.step.results.mock.validate' },
      { icon: LuSparkles,  labelKey: 'blotAnalysis.tour.step.results.mock.calibrate' },
      { icon: LuRefreshCw, labelKey: 'blotAnalysis.tour.step.results.mock.rerun' },
      { icon: LuDownload,  labelKey: 'blotAnalysis.tour.step.results.mock.export' },
      { icon: LuFileText,  labelKey: 'blotAnalysis.tour.step.results.mock.formats' },
    ]
    return (
      <VStack align="stretch" spacing={3}>
        <Text fontSize="sm" color="gray.700" lineHeight="1.6">{body}</Text>

        {/* Mock preview — visual layout of the case-details screen */}
        <Box
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          p={2.5}
        >
          <HStack spacing={2} align="stretch" mb={2}>
            {/* Fake source image with 3 blot rectangles */}
            <Box
              flex={1.6}
              bg="white"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              minH="70px"
              position="relative"
              overflow="hidden"
            >
              <Box position="absolute" top="8px" left="10%" w="18%" h="70%" border="1.5px solid" borderColor="cyan.400" borderRadius="sm" bg="cyan.50" />
              <Box position="absolute" top="8px" left="42%" w="18%" h="70%" border="1.5px solid" borderColor="cyan.400" borderRadius="sm" bg="cyan.50" />
              <Box position="absolute" top="8px" left="74%" w="18%" h="70%" border="1.5px solid" borderColor="cyan.400" borderRadius="sm" bg="cyan.50" />
              <Badge position="absolute" bottom="4px" left="4px" fontSize="2xs" colorScheme="cyan" variant="subtle">image annotée</Badge>
            </Box>
            {/* Fake gallery cards */}
            <VStack flex={1} spacing={1} align="stretch">
              {[0, 1, 2].map((i) => (
                <HStack key={i} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200" px={2} py={1} spacing={2} h="20px">
                  <Box w="14px" h="12px" bg="cyan.100" borderRadius="sm" />
                  <Box flex={1} h="6px" bg="gray.100" borderRadius="full" />
                  <Box w="20px" h="10px" borderRadius="sm" bg={i === 0 ? 'green.200' : i === 1 ? 'orange.200' : 'gray.200'} />
                </HStack>
              ))}
            </VStack>
          </HStack>
          {/* Fake action pills */}
          <HStack spacing={1.5} flexWrap="wrap" justify="center">
            {actions.map((a) => (
              <HStack
                key={a.labelKey}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="full"
                px={2}
                py={0.5}
                spacing={1}
              >
                <Icon as={a.icon} boxSize={2.5} color="teal.500" />
                <Text fontSize="2xs" color="gray.700" fontWeight="600">
                  {t(a.labelKey)}
                </Text>
              </HStack>
            ))}
          </HStack>
        </Box>
      </VStack>
    )
  }

  return <Text fontSize="sm" color="gray.700" lineHeight="1.65">{body}</Text>
}

function computeTooltipPos(rect: DOMRect | null, isMobile: boolean, tooltipH = 320, tooltipWidth = TOOLTIP_W) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const w = Math.min(tooltipWidth, vw - 24)

  if (!rect || isMobile) {
    return {
      top: Math.max(24, vh / 2 - tooltipH / 2),
      left: Math.max(12, vw / 2 - w / 2),
    }
  }

  const spaceBelow = vh - rect.bottom
  const spaceAbove = rect.top
  const placeBelow = spaceBelow >= tooltipH + TOOLTIP_GAP || spaceBelow >= spaceAbove

  let top = placeBelow
    ? rect.bottom + TOOLTIP_GAP
    : rect.top - tooltipH - TOOLTIP_GAP
  top = Math.max(12, Math.min(top, vh - tooltipH - 12))

  // Horizontally try to center on the target, clamp to viewport
  let left = rect.left + rect.width / 2 - w / 2
  left = Math.max(12, Math.min(left, vw - w - 12))

  return { top, left }
}

// ── Welcome modal — workflow overview as 4 phase cards ───────────────────

function WelcomeModal({ onStart, onSkip }: { onStart: () => void; onSkip: () => void }) {
  const { t } = useTranslation()
  const phases = useMemo(() => ([
    { icon: LuUpload,       color: 'teal',    titleKey: 'blotAnalysis.tour.welcome.phase1.title', bodyKey: 'blotAnalysis.tour.welcome.phase1.body' },
    { icon: LuSparkles,     color: 'cyan',    titleKey: 'blotAnalysis.tour.welcome.phase2.title', bodyKey: 'blotAnalysis.tour.welcome.phase2.body' },
    { icon: LuCircleCheck,  color: 'green',   titleKey: 'blotAnalysis.tour.welcome.phase3.title', bodyKey: 'blotAnalysis.tour.welcome.phase3.body' },
    { icon: LuDownload,     color: 'purple',  titleKey: 'blotAnalysis.tour.welcome.phase4.title', bodyKey: 'blotAnalysis.tour.welcome.phase4.body' },
  ]), [])

  return (
    <Modal isOpen onClose={onSkip} size="2xl" isCentered motionPreset="scale">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader
          bgGradient="linear(135deg, teal.600 0%, teal.500 45%, cyan.400 100%)"
          color="white"
          py={6}
          position="relative"
        >
          <HStack spacing={4} position="relative" zIndex={1}>
            <Box
              w="52px" h="52px"
              borderRadius="xl"
              bg="whiteAlpha.300"
              border="1px solid"
              borderColor="whiteAlpha.500"
              display="grid"
              sx={{ placeItems: 'center' }}
              backdropFilter="blur(10px)"
            >
              <Icon as={LuRocket} boxSize={6} />
            </Box>
            <VStack align="start" spacing={0.5}>
              <Text fontSize="xs" letterSpacing="wider" textTransform="uppercase" color="whiteAlpha.900" fontWeight="700">
                {t('blotAnalysis.tour.welcome.eyebrow')}
              </Text>
              <Heading size="md" letterSpacing="tight">
                {t('blotAnalysis.tour.welcome.title')}
              </Heading>
            </VStack>
          </HStack>
          <Box
            position="absolute" top={0} left={0} right={0} bottom={0}
            background="radial-gradient(circle at 85% 15%, rgba(255,255,255,0.25) 0%, transparent 55%)"
            pointerEvents="none"
          />
        </ModalHeader>

        <ModalBody py={6} px={6}>
          <Text fontSize="sm" color="gray.700" mb={5} lineHeight="1.6">
            {t('blotAnalysis.tour.welcome.subtitle')}
          </Text>

          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
            {phases.map((p, i) => (
              <Box
                key={i}
                bg="white"
                border="1px solid"
                borderColor="gray.100"
                borderRadius="xl"
                p={4}
                position="relative"
                transition="all 0.25s ease"
                _hover={{ transform: 'translateY(-2px)', shadow: 'md', borderColor: `${p.color}.200` }}
              >
                <HStack spacing={3} align="start">
                  <Box
                    w="38px" h="38px"
                    borderRadius="lg"
                    bg={`${p.color}.50`}
                    color={`${p.color}.600`}
                    display="grid"
                    sx={{ placeItems: 'center' }}
                  >
                    <Icon as={p.icon} boxSize={5} />
                  </Box>
                  <VStack align="start" spacing={1} flex={1}>
                    <HStack spacing={2}>
                      <Box
                        w="18px" h="18px"
                        borderRadius="full"
                        bg={`${p.color}.500`}
                        color="white"
                        display="grid"
                        sx={{ placeItems: 'center' }}
                        fontSize="2xs"
                        fontWeight="800"
                      >
                        {i + 1}
                      </Box>
                      <Text fontSize="sm" fontWeight="700" color="gray.900">{t(p.titleKey)}</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.600" lineHeight="1.5">{t(p.bodyKey)}</Text>
                  </VStack>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
        </ModalBody>

        <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100" gap={3}>
          <Button variant="ghost" onClick={onSkip} color="gray.600">
            {t('blotAnalysis.tour.welcome.skip')}
          </Button>
          <Button
            colorScheme="teal"
            rightIcon={<LuMousePointerClick />}
            onClick={onStart}
            bgGradient="linear(135deg, teal.500, cyan.500)"
            _hover={{ bgGradient: 'linear(135deg, teal.600, cyan.600)' }}
            px={6}
          >
            {t('blotAnalysis.tour.welcome.cta')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// ── Closing modal ────────────────────────────────────────────────────────

function FinishedModal({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  return (
    <Modal isOpen onClose={onDone} size="md" isCentered motionPreset="scale">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalBody pt={8} pb={4} px={6} textAlign="center">
          <Box
            mx="auto" w="64px" h="64px"
            borderRadius="full"
            bgGradient="linear(135deg, teal.500, cyan.500)"
            display="grid"
            sx={{ placeItems: 'center' }}
            color="white"
            mb={4}
            boxShadow="0 12px 32px -8px rgba(20, 184, 166, 0.55)"
          >
            <Icon as={LuCircleCheck} boxSize={8} />
          </Box>
          <Heading size="md" mb={2} color="gray.900">
            {t('blotAnalysis.tour.finished.title')}
          </Heading>
          <Text fontSize="sm" color="gray.600" lineHeight="1.6">
            {t('blotAnalysis.tour.finished.body')}
          </Text>
        </ModalBody>
        <ModalFooter justifyContent="center" pb={6}>
          <Button
            colorScheme="teal"
            onClick={onDone}
            bgGradient="linear(135deg, teal.500, cyan.500)"
            _hover={{ bgGradient: 'linear(135deg, teal.600, cyan.600)' }}
            px={8}
          >
            {t('blotAnalysis.tour.finished.cta')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
